import { z } from "zod";
import { documentLayoutSchema } from "../lib/document-layout";

/** Select options, kept here so the form and any future consumers share them. */
export const JOB_TITLE_OPTIONS = [
  "HR Manager",
  "HR Administrator",
  "People Ops Lead",
  "Recruiter",
  "Founder / Owner",
  "Other",
] as const;

export const DOCUMENT_TONE_OPTIONS = [
  { value: "professional", label: "Professional" },
  { value: "friendly", label: "Friendly" },
  { value: "formal", label: "Formal" },
  { value: "concise", label: "Concise" },
] as const;

export const DATE_FORMAT_OPTIONS = [
  { value: "mdy", label: "MM/DD/YYYY" },
  { value: "dmy", label: "DD/MM/YYYY" },
  { value: "ymd", label: "YYYY-MM-DD" },
] as const;

export const LANGUAGE_OPTIONS = [
  { value: "en-us", label: "English (US)" },
  { value: "en-gb", label: "English (UK)" },
  { value: "fr", label: "French" },
  { value: "es", label: "Spanish" },
  { value: "pt-br", label: "Portuguese (Brazil)" },
] as const;

/** Company headcount ranges, cut at the plans' employee limits (3, 15, 50,
 *  140) so a range maps straight to the plan that fits it. Accounts that
 *  picked one of the older ranges keep it — see `companySizeOptions`. */
export const COMPANY_SIZE_OPTIONS = [
  { value: "1-3", label: "1–3" },
  { value: "4-15", label: "4–15" },
  { value: "16-50", label: "16–50" },
  { value: "51-140", label: "51–140" },
  { value: "141-500", label: "141–500" },
  { value: "500+", label: "500+" },
] as const;

/** The size options, plus `current` when it is a range no longer offered, so
 *  a select still shows what was saved. */
export function companySizeOptions(current: string): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [...COMPANY_SIZE_OPTIONS];
  if (current && !options.some((o) => o.value === current)) {
    options.push({ value: current, label: current.replace("-", "–") });
  }
  return options;
}

/** Common industries for the setup step; anything else is typed in. */
export const INDUSTRY_OPTIONS = [
  "Retail and e-commerce",
  "Hospitality and food",
  "Professional services",
  "Financial services",
  "Healthcare",
  "Education",
  "Technology",
  "Manufacturing",
  "Construction and real estate",
  "Logistics and transport",
  "Agriculture",
  "Oil, gas and energy",
  "Media and entertainment",
  "Non-profit",
] as const;

export const REMOTE_POLICY_OPTIONS = [
  { value: "onsite", label: "On-site" },
  { value: "hybrid", label: "Hybrid" },
  { value: "remote", label: "Fully remote" },
] as const;

export const PAY_FREQUENCY_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "weekly", label: "Weekly" },
] as const;

export const YES_NO_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
] as const;

/**
 * The personal sender/signatory profile returned by `GET /api/profile`. Edited
 * on the General settings page. Employer details live in `CompanyData`
 * (`GET /api/company`) — the single company of the active workspace.
 */
export type ProfileData = {
  displayName: string;
  jobTitle: string;
  phone: string;
  bio: string;
  signature: string;
};

// Personal sender identity, edited in General settings. `fullName` is the only
// required field — it maps to the auth user's name; the rest persist to the
// owner's profile. `signature` is a public R2 URL managed by the signature
// dialog (kept here so a full save round-trips it instead of clearing it).
export const profileFormSchema = z.object({
  fullName: z.string().trim().min(2, "Name must be at least 2 characters").max(120),
  displayName: z.string().trim().max(120),
  jobTitle: z.string().max(120),
  phone: z.string().trim().max(40),
  bio: z.string().trim().max(1000),
  signature: z.string().trim().max(500),
});

export type ProfileFormValues = z.infer<typeof profileFormSchema>;

// Company letterhead + document defaults, edited in Company settings. `logoUrl`
// is a public R2 URL managed by the logo upload control (kept here so a full
// save round-trips it). All fields are present-but-optional (default to "") so
// react-hook-form always has controlled inputs.
export const companyFormSchema = z.object({
  // Identity
  companyName: z.string().trim().max(160),
  tagline: z.string().trim().max(200),
  registrationNumber: z.string().trim().max(80),
  taxId: z.string().trim().max(80),

  // Address & contact
  companyEmail: z.string().trim().max(160).email("Enter a valid email").or(z.literal("")),
  companyPhone: z.string().trim().max(40),
  companyWebsite: z.string().trim().max(200),
  companyAddress: z.string().trim().max(400),
  country: z.string().trim().max(80),
  region: z.string().trim().max(120),
  logoUrl: z.string().trim().max(500),

  // Business profile
  industry: z.string().trim().max(120),
  multiCountry: z.string().trim().max(8),
  companySize: z.string().trim().max(40),

  // Culture & mission
  mission: z.string().trim().max(2000),
  vision: z.string().trim().max(2000),
  coreValues: z.string().trim().max(1000),
  cultureDescription: z.string().trim().max(2000),
  orgStructure: z.string().trim().max(2000),

  // Working policies
  workingHours: z.string().trim().max(120),
  workingDaysPerWeek: z.string().trim().max(40),
  totalHoursPerWeek: z.string().trim().max(40),
  remotePolicy: z.string().trim().max(40),
  annualLeaveDays: z.string().trim().max(40),
  sickLeaveDays: z.string().trim().max(40),
  payFrequency: z.string().trim().max(40),
  paymentMethod: z.string().trim().max(80),

  // Legal & signatory defaults
  governingLaw: z.string().trim().max(160),
  issuerName: z.string().trim().max(120),
  issuerTitle: z.string().trim().max(120),
  preparedBy: z.string().trim().max(160),
  preparedByTitle: z.string().trim().max(120),
  approvedBy: z.string().trim().max(160),
  approvingAuthorityTitle: z.string().trim().max(120),
  ceoNameTitle: z.string().trim().max(160),

  // Locations & departments
  hasBranchOutsideHq: z.string().trim().max(8),
  defaultDepartment: z.string().trim().max(120),

  // The workspace's chosen letterhead template ("" = platform default).
  defaultLetterheadId: z.string().trim().max(64),

  // Document defaults
  documentTone: z.string(),
  dateFormat: z.string(),
  language: z.string(),
  defaultJurisdiction: z.string().trim().max(80),

  // How generated documents are formatted and styled (see `document-layout.ts`).
  documentLayout: documentLayoutSchema,
});

export type CompanyFormValues = z.infer<typeof companyFormSchema>;

/**
 * A persisted company as returned by `GET /api/company` — the workspace's single
 * company: the editable form fields plus the server-assigned `id`.
 */
export type CompanyData = CompanyFormValues & {
  id: string;
  /** Naming (Settings → Naming): what the workspace calls its people and the
   *  sessions they check in to. "" = the default ("Employee(s)", "Session(s)"). */
  peopleLabelSingular?: string;
  peopleLabelPlural?: string;
  sessionLabelSingular?: string;
  sessionLabelPlural?: string;
  /** Settings → Attendance: whether new sessions start with manual check-in allowed. */
  attendanceManualCheckInDefault?: boolean;
  /** Settings → Attendance: the default for checking out anyone still in when HR closes check-in early. */
  attendanceAutoCheckOutDefault?: boolean;
};
